import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1, 0),
    width: "100%"
  },
  colorPicker: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  colorPreview: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    margin: theme.spacing(1, 0),
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    cursor: "pointer"
  }
}));

const EditStageDialog = ({ open, onClose, stage }) => {
  const classes = useStyles();
  
  const [formData, setFormData] = useState({
    name: "",
    color: "#2196f3",
    isInitialStage: false,
    isFinalStage: false
  });
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    if (stage) {
      setFormData({
        name: stage.name || "",
        color: stage.color || "#2196f3",
        isInitialStage: stage.isInitialStage || false,
        isFinalStage: stage.isFinalStage || false
      });
    }
  }, [stage, open]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = e.target.type === "checkbox" ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleColorChange = (color) => {
    setFormData(prev => ({ ...prev, color: color.hex }));
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        toast.error(i18n.t("Stage name is required"));
        return;
      }

      await api.put(`/kanban/stages/${stage.id}`, formData);
      
      toast.success(i18n.t("kanban.notifications.stageUpdated"));
      
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erro ao atualizar estágio");
    }
  };

  if (!stage) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="edit-stage-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="edit-stage-dialog-title">
        {i18n.t("kanban.settings.editStage")}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          name="name"
          label={i18n.t("kanban.settings.stageName")}
          value={formData.name}
          onChange={handleChange}
          fullWidth
          margin="dense"
          variant="outlined"
          className={classes.formControl}
        />
        
        <div>
          <InputLabel shrink>
            {i18n.t("kanban.settings.stageColor")}
          </InputLabel>
          <div 
            className={classes.colorPreview}
            style={{ backgroundColor: formData.color }}
            onClick={toggleColorPicker}
          />
          {showColorPicker && (
            <div className={classes.colorPicker}>
              <ChromePicker 
                color={formData.color}
                onChange={handleColorChange}
                disableAlpha
              />
            </div>
          )}
        </div>
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.isInitialStage}
              onChange={handleChange}
              name="isInitialStage"
              color="primary"
            />
          }
          label={i18n.t("kanban.settings.isInitialStage")}
          className={classes.formControl}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.isFinalStage}
              onChange={handleChange}
              name="isFinalStage"
              color="primary"
            />
          }
          label={i18n.t("kanban.settings.isFinalStage")}
          className={classes.formControl}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("kanban.buttons.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary">
          {i18n.t("kanban.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStageDialog;